import { useCallback, useEffect, useMemo, useState } from 'react';

import { CompactSelection, DataEditor, GridCell, GridCellKind, GridColumn, GridSelection, Item } from '@glideapps/glide-data-grid';

import { ISelectableOption, MessageBar, MessageBarType, PrimaryButton, Spinner } from '@fluentui/react';

import { DateTime } from 'luxon';
import { t } from 'i18next';

import TimeTaxSupportSystemClient from '../../../../../system';
import { User } from '../../../../../system/User';
import MessageCourier from '../../../../../lib/MessageCourier';

import { LiquidacionInsumo, PlantillaDerivacionCostos } from '../../../../../system/settlement/aje/liquidacion-insumos';

import { AgrupadorCorporacionLiquidadora } from '../../../../../system/agrupador_corporacion_liquidadora/AgrupadorCorporacionLiquidadoraSystem';

import { handleColumnResizeUtil } from '../../../../../lib/GlideDataGrid/UtilsGlideGrid';

interface Props {
  system: TimeTaxSupportSystemClient;
  courier: MessageCourier;
  changeBlockUI: (state: boolean) => void;
  user: User;
  from: DateTime;
  setButtonDisability: (can: boolean) => void;
  settlementType: ISelectableOption<AgrupadorCorporacionLiquidadora>;
  liquidacionInsumosList: LiquidacionInsumo[];
}

interface RowData {
  [key: string]: string;
}

const EMPTY_SELECTION: GridSelection = {
  columns: CompactSelection.empty(),
  rows: CompactSelection.empty(),
  current: undefined,
};

export const SettlementsDerivation = (props: Props) => {
  const [rows, setRows] = useState<RowData[]>([]);
  const [originalRows, setOriginalRows] = useState<RowData[]>([]);
  const [columns, setColumns] = useState<GridColumn[]>([]);
  const [gridSelection, setGridSelection] = useState<GridSelection>(EMPTY_SELECTION);

  const [loadingFetch, setLoadingFetch] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);

  const [plantilla, setPlantilla] = useState<PlantillaDerivacionCostos | null>(null);

  const hasInvalidRows = useMemo(() => rows.some((row) => Object.values(row).some((value) => value.trim().length < 3)), [rows]);
  const hasChanges = useMemo(() => rows.length > 0 && JSON.stringify(rows) !== JSON.stringify(originalRows), [rows, originalRows]);

  const isSaveDisabled = useMemo(() => {
    return hasInvalidRows || !hasChanges || !plantilla;
  }, [hasInvalidRows, hasChanges, plantilla]);

  const clearSelection = useCallback(() => {
    setGridSelection(EMPTY_SELECTION);
  }, []);

  /**
   * Fetch inicial
   */
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoadingFetch(true);
        const data = await props.system.getSettlementSystem().getPlantillaDerivacionCostos();
        setPlantilla(data);
        if (data) {
          setRows(data.rows);
          setOriginalRows(structuredClone(data.rows));
        }
      } catch (error) {
        props.courier.messageError(error);
      } finally {
        setLoadingFetch(false);
      }
    };

    fetchData();
  }, []);

  /**
   * Habilitar / deshabilitar botón padre
   */
  useEffect(() => {
    if (hasChanges || rows.length === 0 || plantilla === null) {
      props.setButtonDisability(false);
      return;
    }

    props.setButtonDisability(!!plantilla);
  }, [hasChanges, rows.length, plantilla]);

  /**
   * Generar columnas
   */
  useEffect(() => {
    if (!rows.length) return;

    setColumns(
      Object.keys(rows[0]).map((key) => ({
        title: key,
        width: 200,
      })),
    );
  }, [rows]);

  const getCellContent = useCallback(
    ([col, row]: Item): GridCell => {
      const columnName = columns[col]?.title;

      const value = rows[row]?.[columnName as keyof RowData] ?? '';

      return {
        kind: GridCellKind.Text,
        data: value,
        displayData: value,
        readonly: false,
        allowOverlay: true,
      };
    },
    [rows, columns],
  );

  const onCellEdited = useCallback(
    ([col, row]: Item, value: GridCell) => {
      if (value.kind !== GridCellKind.Text) return;

      const columnName = columns[col].title;

      setRows((prev) => {
        const current = prev[row][columnName];

        if (current === value.data) return prev;

        const updated = [...prev];

        updated[row] = {
          ...updated[row],
          [columnName]: value.data,
        };

        return updated;
      });
    },
    [columns],
  );

  const handleDeleteRows = useCallback(() => {
    const selectedRows = [...gridSelection.rows];

    if (!selectedRows.length) return;

    setRows((prev) => prev.filter((_, index) => !selectedRows.includes(index)));

    clearSelection();
  }, [gridSelection, clearSelection]);

  const handleAddRow = useCallback(() => {
    const newRow = columns.reduce((acc, col) => {
      acc[col.title] = '';
      return acc;
    }, {} as RowData);

    setRows((prev) => [...prev, newRow]);

    clearSelection();
  }, [columns, clearSelection]);

  const handleSaveChanges = async () => {
    if (!plantilla || !props.settlementType.data) return;

    try {
      setLoadingSave(true);
      props.changeBlockUI(true);
      await props.system.getSettlementSystem().procesarPlantillaDerivacionCostos(rows);
      props.courier.messageSuccess(t('Cambios guardados exitosamente'));
      setOriginalRows(structuredClone(rows));
    } catch (error) {
      props.courier.messageError(error);
    } finally {
      setLoadingSave(false);

      props.changeBlockUI(false);
    }
  };

  const handleColumnResize = useCallback(handleColumnResizeUtil(setColumns), []);

  if (loadingFetch) {
    return (
      <div className="mt-2 mb-2">
        <Spinner label={t('Cargando plantilla de derivacion de costos...')} />
      </div>
    );
  }

  if (!plantilla) {
    return (
      <div className="mt-2 mb-2">
        <MessageBar messageBarType={MessageBarType.warning}>
          {t('No se ha cargado la plantilla de derivación de costos, Comuniquese con su administrador para que realize su carga.')}
        </MessageBar>
      </div>
    );
  }

  return (
    <div className="mt-2 mb-2" dir="ltr">
      <DataEditor
        columns={columns}
        rows={rows.length}
        getCellContent={getCellContent}
        onCellEdited={onCellEdited}
        onColumnResize={handleColumnResize}
        rowMarkers="both"
        columnSelect="multi"
        smoothScrollX
        smoothScrollY
        gridSelection={gridSelection}
        onGridSelectionChange={setGridSelection}
      />

      <div style={{ display: 'flex', gap: 8 }}>
        <PrimaryButton className="mt-2" text={t('Agregar Fila')} onClick={handleAddRow} />
        <PrimaryButton className="mt-2" text={t('Eliminar')} onClick={handleDeleteRows} disabled={[...gridSelection.rows].length === 0} />
        <PrimaryButton className="mt-2" text={t('Guardar')} disabled={isSaveDisabled || loadingSave} onClick={handleSaveChanges} />
      </div>
    </div>
  );
};
